import { MaterialTopTabBarProps } from '@react-navigation/material-top-tabs';
import { useIsFocused } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeviceEventEmitter,
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import Toast, { useToast } from 'react-native-toast-notifications';

import CustomTabBarProject from '@components/CustomTabBar/CustomTabBarProject';
import { StatusToast } from '@components/CustomToast/StatusToast.web';
import { ErrorBoundary } from '@components/ErrorBoundary';
import { HyperLink } from '@components/HyperLink';
import { InviteRequestButton } from '@components/Invite/InviteRequestButton';
import { ConfirmModal } from '@components/Modals/ConfirmModal';
import { ProjectChecklistPage } from '@components/ProjectDetail/ProjectChecklist/ProjectChecklist';
import { ProjectDetailTabsSkeleton } from '@components/ProjectDetail/ProjectDetailTabsSkeleton.web';
import { GotCrewForThisJobBanner } from '@components/Projects/GotCrewForThisJobBanner';
import { ProjectFilesSelectionBar } from '@components/Projects/ProjectFilesSelectionBar.web';
import { removeProjectLimitSnackBarNextTime } from '@components/Projects/ProjectForm.web';
import { Box, Text } from '@components/Restyle';
import {
  BottomToast,
  BottomToastHandle,
} from '@components/shared/BottomToast.web';
import Button from '@components/shared/Button/Button';
import Icon from '@components/shared/Icon/Icon';
import { RestyleProps } from '@components/shared/Icon/RestyleIcon';
import { MenuItem } from '@components/shared/Popup/PopupItem';
import { UpgradePaywallTooltip } from '@components/shared/UpgradePaywallTooltip';
import { TaskCreatedToast } from '@components/TaskList/TaskCreatedToast.web';
import { TooltipWebV3 } from '@components/TaskList/TooltipWebV3.web';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import { createTabNavigator } from '@components/Web/Navigator/TabNavigator';
import { ProjectCreatedModalWeb } from '@components/Web/ProjectCreatedModal.web';
import { TooltipOnboardingWeb } from '@components/Web/TooltipOnboarding.web';
import {
  GetMeDocument,
  Project,
  Task,
  Team,
  TeamMemberRole,
  WebTutorialFlow,
  useCompleteWebTutorialFlowsMutation,
  useCreateInviteMutation,
  useDeleteProjectMutation,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import useMe from '@hooks/useMe';
import { useMinimumVisible } from '@hooks/useMinimumVisible';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useProjectFromQuery } from '@hooks/useProjectFromQuery';
import { useProjectMembership } from '@hooks/useProjectMembership';
import { useProjectOnboarding } from '@hooks/useProjectOnboarding';
import { useWebDrawerNavigator } from '@hooks/useWebDrawerNavigator';
import { NestedNavigatorParams } from '@navigation/helpers';
import { ProjectStackScreenProps } from '@navigation/projects/projects-stack.web';
import { TaskPanelParamsList } from '@navigation/tasks/task-panel';
import { useAppNavigation } from '@navigation/useAppNavigation';
import ProjectActivityLog from '@screens/Projects/ProjectActivityLog';
import ProjectFiles, {
  ProjectFilesRef,
} from '@screens/Projects/ProjectFiles.web';
import { ProjectMembers } from '@screens/Projects/ProjectMembers.web';
import { ProjectOnboardingCard } from '@screens/Projects/ProjectOnboardingCard';
import { ProjectTasks } from '@screens/Projects/ProjectTasks.web';
import { ProjectWelcomeMobileScreen } from '@screens/Projects/ProjectWelcomeMobileScreen.web';
import { ProjectDetailBanner } from '@src/navigation/projects/projectDetailBanner';
import theme, { getScaledSize, SCALED_DIMENSIONS } from '@themes/theme';
import {
  CLIPBOARD_FAILURE_MESSAGE_KEYS,
  copyTextToClipboard,
} from '@utils/copyTextToClipboard';
import { APP_STORE_URL, detectAppInstallation } from '@utils/mobileDeepLink';
import { shouldShowPaywallForProject } from '@utils/teams/paywall';

const isMobileBrowser =
  Platform.OS === 'web' &&
  typeof globalThis.window !== 'undefined' &&
  /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    globalThis.navigator.userAgent
  );

type CopyLinkTooltipMode = 'hover' | 'copied' | null;

export type ProjectDetailTabsParamList = {
  'project-details': undefined;
  'activity-log': {
    projectUUId: Project['uuid'];
  };
  tasks: {
    projectUUId: Project['uuid'];
    taskId?: Task['id'];
    isFromMyTask?: boolean;
  } & NestedNavigatorParams<TaskPanelParamsList>;
  checklist: {
    projectUUId: Project['uuid'];
    showActionTooltip?: boolean;
  };
  members: {
    projectUUId: Project['uuid'];
    openAddMembers?: boolean;
  };
  files: {
    projectUUId: Project['uuid'];
  };
};

export type ProjectDetailTabsScreenProps<
  T extends keyof ProjectDetailTabsParamList,
> = StackScreenProps<ProjectDetailTabsParamList, T>;

const Tabs = createTabNavigator<ProjectDetailTabsParamList>();

type BuildMenuListParams = {
  isAdminOrHigher: boolean;
  isEditorOrHigher: boolean;
  isTeamOwnerOrAdmin: boolean;
  isTeamMember: boolean;
  isMember: boolean;
  hasMembers: boolean;
  t: (key: string) => string;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

function buildMenuList({
  isAdminOrHigher,
  isEditorOrHigher,
  isTeamOwnerOrAdmin,
  isTeamMember,
  isMember,
  hasMembers,
  t,
  onEdit,
  onDuplicate,
  onDelete,
}: BuildMenuListParams): MenuItem[] {
  const items: MenuItem[] = [];

  if (isAdminOrHigher || isTeamOwnerOrAdmin) {
    items.push({
      iconName: 'Edit',
      name: t('projects.edit.title'),
      onSelect: onEdit,
    } as MenuItem);
  }

  if (isEditorOrHigher) {
    items.push({
      iconName: 'Duplicate',
      disable: !(isTeamMember || isTeamOwnerOrAdmin),
      name: 'Duplicate',
      onSelect: onDuplicate,
    } as MenuItem);
  }

  if ((isEditorOrHigher || isTeamOwnerOrAdmin) && hasMembers) {
    items.push({
      iconName: 'MessageSquare',
      name: 'Announcement',
      isComingSoon: true,
    } as MenuItem);
  }

  items.push({
    iconName: 'Share',
    name: 'Share',
    isComingSoon: true,
  } as MenuItem);

  if (!isTeamOwnerOrAdmin || (isTeamOwnerOrAdmin && isMember)) {
    items.push({
      iconName: 'LogOut',
      name: 'Leave',
      isComingSoon: true,
    } as MenuItem);
  }

  if (isAdminOrHigher || !isTeamMember) {
    items.push(
      { iconName: 'Archive', name: 'Archive', isComingSoon: true } as MenuItem,
      {
        iconName: 'Trash2',
        name: 'Delete',
        isRed: true,
        isComingSoon: false,
        onSelect: onDelete,
      } as MenuItem
    );
  }

  return items;
}

// ── CustomButton ──────────────────────────────────────────────────────────────

interface CustomButtonProps {
  backgroundColor: RestyleProps['backgroundColor'];
  prefix?: React.ReactNode;
  onPress: () => void;
  disabled?: boolean;
  label: string;
  variant: 'copy' | 'create';
  accessibilityLabel: string;
  borderWidth?: number;
  borderColor?: keyof typeof theme.colors;
  btnTextColor?: keyof typeof theme.colors;
  borderRadius?: keyof typeof theme.borderRadii;
}

const CustomButton: React.FC<CustomButtonProps> = ({
  backgroundColor,
  prefix,
  onPress,
  label,
  disabled,
  variant,
  accessibilityLabel,
  borderWidth,
  borderColor,
  btnTextColor,
  borderRadius,
}) => (
  <Button
    disabled={disabled}
    backgroundColor={backgroundColor}
    prefix={prefix}
    variant={variant}
    onPress={onPress}
    isSmall
    height={getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT)}
    accessibilityLabel={accessibilityLabel}
    borderWidth={borderWidth}
    borderColor={borderColor}
    btnTextColor={btnTextColor}
    borderRadius={borderRadius}>
    {label}
  </Button>
);

// ── ProjectFilesScreen ────────────────────────────────────────────────────────

interface ProjectFilesScreenContextValue {
  filesPanelRef: React.RefObject<ProjectFilesRef | null>;
  onSelectionStateChange: (state: ProjectFilesRef) => void;
}

const ProjectFilesScreenContext =
  React.createContext<ProjectFilesScreenContextValue | null>(null);

function ProjectFilesScreen(props: any) {
  const ctx = React.useContext(ProjectFilesScreenContext);
  if (!ctx) return null;
  return (
    <ProjectFiles
      {...props}
      ref={ctx.filesPanelRef}
      onSelectionStateChange={ctx.onSelectionStateChange}
    />
  );
}

// ── InviteButtonContent ───────────────────────────────────────────────────────

interface InviteButtonContentProps {
  mainPanelWidth: number;
  archivedAt: boolean;
  inviteHovered: boolean;
  circleInviteBgColor: string;
  onPress: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  addMembersLabel: string;
  inviteLabel: string;
}

function InviteButtonContent({
  mainPanelWidth,
  archivedAt,
  inviteHovered,
  circleInviteBgColor,
  onPress,
  onMouseEnter,
  onMouseLeave,
  addMembersLabel,
  inviteLabel,
}: InviteButtonContentProps) {
  const iconColor: RestyleProps['color'] = archivedAt ? 'grey04' : 'black';
  const bgForCustomButton: RestyleProps['backgroundColor'] =
    !archivedAt && inviteHovered ? 'grey01' : 'white';
  return (
    <Box onMouseEnter={onMouseEnter as any} onMouseLeave={onMouseLeave as any}>
      {mainPanelWidth < 800 ? (
        <button
          disabled={archivedAt}
          aria-label={addMembersLabel}
          onClick={onPress}
          style={{
            width: getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT),
            height: getScaledSize(SCALED_DIMENSIONS.BUTTON_HEIGHT),
            borderRadius: '50%',
            border: archivedAt ? 'none' : `1px solid ${theme.colors.black}`,
            backgroundColor: circleInviteBgColor,
            cursor: archivedAt ? 'default' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
            flexShrink: 0,
          }}>
          <Icon variant='s' name='UserPlus' color={iconColor} />
        </button>
      ) : (
        <CustomButton
          disabled={archivedAt}
          backgroundColor={bgForCustomButton}
          variant={'edit' as any}
          borderWidth={archivedAt ? 0 : 1}
          borderColor={archivedAt ? 'grey04' : 'black'}
          btnTextColor={iconColor}
          borderRadius='l'
          prefix={<Icon variant='s' name='UserPlus' color={iconColor} />}
          onPress={onPress}
          label={inviteLabel}
          accessibilityLabel={addMembersLabel}
        />
      )}
    </Box>
  );
}

type NonMemberAssignee = {
  email: string;
  id?: string;
  name?: string;
  avatarId?: string;
};

const readStoredNonMemberAssignees = (
  projectId: string | undefined
): NonMemberAssignee[] => {
  const storedProjectId = globalThis.localStorage?.getItem(
    'non_member_assignee_emails_project_id'
  );
  if (!projectId || storedProjectId !== projectId) return [];
  try {
    const parsed = JSON.parse(
      globalThis.localStorage?.getItem('non_member_assignee_emails') ?? '[]'
    ) as unknown;
    return Array.isArray(parsed)
      ? parsed
          .map((a) => (typeof a === 'string' ? { email: a } : a))
          .filter(
            (a): a is NonMemberAssignee =>
              !!a && typeof a.email === 'string' && !!a.email
          )
      : [];
  } catch {
    return [];
  }
};

const getNonMemberBannerDescription = (
  assignees: NonMemberAssignee[],
  t: (key: string, opts?: Record<string, unknown>) => string
): string | undefined => {
  if (assignees.length === 1) {
    return t('nudge.gotCrewForThisJob.nonMemberSingleDescription', {
      identifier: assignees[0].name || assignees[0].email,
    });
  }
  if (assignees.length > 1) {
    return t('nudge.gotCrewForThisJob.nonMemberMultipleDescription', {
      count: assignees.length,
    });
  }
  return undefined;
};

export const ProjectDetailTabs = ({
  route,
}: ProjectStackScreenProps<'details'>) => {
  const { me } = useMe();
  const onboardingActive = useOnboardingActive();
  const toast = useToast();
  const { width: windowWidth } = useWindowDimensions();
  const { setHeaderTitle, setSelectedProject } = useWebDrawerNavigator();
  const { t } = useTranslation('models');
  const navigation = useAppNavigation();
  const [_headerWidth, setHeaderWidth] = useState(0);
  const [_tabsWidth, setTabsWidth] = useState(0);
  const [projectId, setProjectId] = useState<string>();
  const toastRef = useRef(null);
  const checklistTabRef = useRef<any>(null);
  const tabNavigationRef = useRef<any>(null);
  const [copyLinkTooltipMode, setCopyLinkTooltipMode] =
    useState<CopyLinkTooltipMode>(null);
  const [suppressHoverTooltip, setSuppressHoverTooltip] = useState(false);
  const [inviteHovered, setInviteHovered] = useState(false);
  // Ghost icon hover tint (TA-9005): tracked separately from the tooltip
  // state machine so the grey01 tint still shows while the tooltip is
  // suppressed after a copy.
  const [isCopyLinkHovered, setIsCopyLinkHovered] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Same red-icon failure toast as MediaViewer's copy-link (TA-10845).
  const copyFailedToastRef = useRef<BottomToastHandle>(null);
  const projectFilesRef = useRef<ProjectFilesRef>(null);
  const [projectFilesState, setProjectFilesState] =
    useState<ProjectFilesRef | null>(null);

  const [showInviteNudge, setShowInviteNudge] = useState(false);
  const [isInviteFromBanner, setIsInviteFromBanner] = useState(false);
  const [passMemberAssigneeEmails, setPassMemberAssigneeEmails] =
    useState<boolean>(false);
  const [nonMemberAssigneeEmails, setNonMemberAssigneeEmails] = useState<
    NonMemberAssignee[]
  >([]);
  const isFocused = useIsFocused();

  // Callback to update state when selection changes
  const handleSelectionStateChange = useCallback((state: ProjectFilesRef) => {
    setProjectFilesState(state);
  }, []);

  const [requestSentSheetVisible, setRequestSentSheetVisible] = useState(false);

  const { currentProject, goToProject } = useProjectOnboarding();

  const {
    params: { projectUUId },
  } = route;

  const _UUID_REGEX =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

  const { project, loading } = useProjectFromQuery({
    projectUUId,
    fetchPolicy: 'cache-and-network',
  });

  // TA-10616: `loading` is true on every mount under cache-and-network, so
  // gating the whole region on it alone flashed a full-region spinner even
  // on a warm cache. Only placeholder when there is genuinely nothing to
  // render yet — and fold in the TA-10313 partial-cache guard (project
  // arrived from cache but members hasn't been fetched yet; without members,
  // isMemberOrOwner evaluates false and the non-member branch renders an
  // empty Activity Log).
  const isProjectShellLoading =
    (loading && !project) || (!!project && !Array.isArray(project.members));
  const showShellSkeleton = useMinimumVisible(isProjectShellLoading);

  const {
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setIsTeamPersonal,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setTeamValues,
    setUpgradeConfirmModalOpen,
    setPaywallModalOpen,
    setPaywallTeam,
    mainPanelWidth,
    showProjectCreatedModal,
    setShowProjectCreatedModal,
  } = useWebDrawer();

  useEffect(() => {
    if (typeof globalThis.window !== 'undefined') {
      const flag = globalThis.sessionStorage.getItem('pending_request_sent');
      if (flag === projectUUId) {
        globalThis.sessionStorage.removeItem('pending_request_sent');
        // Bottom sheet is mobile-web only — desktop gets no modal
        if (isMobileBrowser) setRequestSentSheetVisible(true);
      }
    }
  }, [projectUUId]);

  // Open the invited task's panel after redirect from task invite signup.
  // Runs when the project first loads (project?.id transitions from undefined).
  // Works for both members and non-members: TaskPolicy#show? allows task members
  // (TaskUser records created during invite acceptance) to view the task.
  useEffect(() => {
    if (!project?.id) return;
    const pendingTaskId = window.sessionStorage.getItem(
      'post_invite_open_task_id'
    );
    if (pendingTaskId) {
      window.sessionStorage.removeItem('post_invite_open_task_id');
      setIdTaskDetailOpen(pendingTaskId);
      setIsTaskWebPanelOpen(true);
    }
  }, [project?.id]);

  useEffect(() => {
    // Guard on project?.id to prevent infinite loop from object reference changes
    if (project?.id && project.id !== projectId) {
      setProjectId(project.id);
      setSelectedProject(project);
    }
  }, [project?.id, project, projectId, setSelectedProject]);

  useEffect(() => {
    if (project?.name) {
      setHeaderTitle(project?.name);
    }
    return () => {
      (!navigation.isFocused() && setHeaderTitle(undefined)) || void 0;
      if (!navigation.isFocused()) {
        setSelectedProject(undefined);
      }
    };
  }, [project?.name]);

  useEffect(() => {
    const unsubscribe = navigation
      .getParent()
      ?.addListener('drawerItemPress', (e) => {
        navigation.reset({ routes: [{ name: 'allprojects' }], index: 0 });
        e.preventDefault();
      });

    return unsubscribe;
  }, [navigation]);

  useEffect(() => {
    const handlePopState = () => {
      const currentPath = globalThis.location.pathname;
      const projectPathRegex =
        /\/projects\/[^/]+\/(activity-log|tasks|members|files)/;

      if (projectPathRegex.test(currentPath)) {
        globalThis.history.pushState(null, '', globalThis.location.pathname);
        navigation.navigate('projects-stack', {
          screen: 'details',
          params: { projectUUId },
        });
      }
    };

    globalThis.addEventListener('popstate', handlePopState);

    return () => {
      globalThis.removeEventListener('popstate', handlePopState);
    };
  }, [navigation, projectUUId]);

  const { members, description } = project || {};
  const {
    isMemberOrOwner,
    isAdminOrHigher,
    isEditorOrHigher,
    isMember,
    isOwner,
  } = useProjectMembership(project);

  function checkRemainingDays(targetDateStr: string | number | Date) {
    const targetDate = new Date(targetDateStr);
    const currentDate = new Date();

    const currentUTC = new Date(
      Date.UTC(
        currentDate.getUTCFullYear(),
        currentDate.getUTCMonth(),
        currentDate.getUTCDate()
      )
    );

    const targetUTC = new Date(
      Date.UTC(
        targetDate.getUTCFullYear(),
        targetDate.getUTCMonth(),
        targetDate.getUTCDate()
      )
    );

    const diffInMs = targetUTC.getTime() - currentUTC.getTime();
    const diffInDays = Math.round(diffInMs / (1000 * 60 * 60 * 24));

    if (!Number.isNaN(diffInDays)) return diffInDays;

    return undefined;
  }

  const daysRemaining = checkRemainingDays(
    project?.team?.subscriptionDetails?.expirationDate
  );

  const goToUpgradeSubscription = (team: Team) => {
    setIsTaskWebPanelOpen(false);
    setIsTaskWebPanelMaximize(false);
    setIdTaskDetailOpen('');
    setIdProjectOfTaskDetailOpen('');
    setIsCreateProjectOpen(false);
    setEditProjectUUId('');
    setIdTeamOpen(team?.id ?? '');
    setUUIdTeamOpen(team?.uuid ?? '');
    setIsTeamPersonal(team?.personal ?? false);
    setTeamValues(team);
    setUpgradeConfirmModalOpen(true);
  };

  useEffect(() => {
    const dateRemaining = checkRemainingDays(
      project?.team?.subscriptionDetails?.expirationDate
    );

    if (
      project?.team?.subscriptionDetails?.isSubscriptionCanceled &&
      dateRemaining &&
      dateRemaining <= 0
    ) {
      setShowBanner(true);
      return;
    }
    if (
      project?.team?.subscriptionDetails?.isSubscriptionOnTrial &&
      project?.team?.subscriptionDetails?.expirationDate &&
      dateRemaining &&
      dateRemaining <= 7 &&
      (project?.team?.subscriptionDetails?.paymentCardLast4 === null ||
        project?.team?.subscriptionDetails?.paymentCardLast4 === undefined)
    ) {
      setShowBanner(true);
    }
  }, [
    project?.team?.subscriptionDetails?.expirationDate,
    project?.team?.subscriptionDetails?.isSubscriptionCanceled,
  ]);

  const isTeamMember = project?.currentUserTeamRole === 'MEMBER';
  const isTeamOwnerOrAdmin =
    project?.currentUserTeamRole === 'OWNER' ||
    project?.currentUserTeamRole === 'ADMIN';

  const _nonMember =
    !(isMemberOrOwner || isTeamOwnerOrAdmin || isTeamMember) ||
    project?.archivedAt;

  const copyToClipboard = useCallback(
    async (link: string) => {
      if (!link) return;
      const result = await copyTextToClipboard(link);
      if (!result.success) {
        copyFailedToastRef.current?.show(
          t(CLIPBOARD_FAILURE_MESSAGE_KEYS[result.reason]),
          { duration: 3000, icon: 'warning' }
        );
        return;
      }

      setSuppressHoverTooltip(true);
      setCopyLinkTooltipMode('copied');

      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }

      copiedTimerRef.current = setTimeout(() => {
        setCopyLinkTooltipMode(null);
      }, 3000);
    },
    [t]
  );

  const [completeWebTutorialFlows] = useCompleteWebTutorialFlowsMutation({
    refetchQueries: [{ query: GetMeDocument }, 'getMe'],
  });

  const [updateOnboardingState] = useUpdateOnboardingStateMutation();

  const [createInviteMutation, { data: createInviteData }] =
    useCreateInviteMutation();

  useEffect(() => {
    if (!createInviteData) return;
    const { createInvite } = createInviteData;
    if (createInvite) {
      const formattedLink = createInvite.replace(
        'register/register-with-email',
        'invited-user'
      );
      copyToClipboard(formattedLink);
    }
  }, [createInviteData]);

  const handleCreateInvite = () => {
    if (!me?.completedWebTutorialFlows?.includes('invite_member')) {
      completeWebTutorialFlows({
        variables: {
          flowType: WebTutorialFlow.InviteMember,
        },
      });
    }
    createInviteMutation({
      variables: { projectId: projectUUId ?? projectId },
    });
  };

  const [
    deleteProject,
    {
      loading: isDeletingProject,
      data: deleteProjectData,
      error: deleteProjectError,
    },
  ] = useDeleteProjectMutation({
    refetchQueries: ['listProjects', 'listProjectsRecent'],
  });

  useEffect(() => {
    if (!deleteProjectData) return;
    removeProjectLimitSnackBarNextTime(me?.id, '0');
    removeProjectLimitSnackBarNextTime(me?.id, project?.teamId);
    setShowModal(false);
    navigation.goBack();
    // `navigation` (useAppNavigation()) is deliberately excluded — it
    // rebuilds a brand-new object on every render (see useAppNavigation.ts),
    // so listing it here would refire this effect (and call goBack() again)
    // on any unrelated re-render, including the one setShowModal(false)
    // itself just triggered. Confirmed by a real double-goBack() test
    // failure when this was added naively (PR #5566 review).
  }, [deleteProjectData, me?.id, project?.teamId]);

  useEffect(() => {
    if (!deleteProjectError) return;
    // Deliberately leave the modal open on failure — the global spinner
    // rule (see TA-10089 plan) is "restore button + inline error", and
    // closing here would silently discard the user's delete intent.
    toast.show(
      <StatusToast
        message={t('deleteFailedTryAgain')}
        duration={3000}
        type='error'
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: { backgroundColor: 'transparent', shadowColor: 'transparent' },
      }
    );
  }, [deleteProjectError, toast, t]);

  const [modal, setShowModal] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [showChecklistTooltip, setShowChecklistTooltip] = useState(false);
  const [isChecklistActionTooltipActive, setIsChecklistActionTooltipActive] =
    useState(false);
  const isTaskNameTooltipShowingRef = useRef(false);
  const isFocusedRef = useRef(isFocused);
  const showChecklistTooltipRef = useRef(showChecklistTooltip);
  const meRef = useRef(me);
  const skipChecklistCompleteOnNextBlurRef = useRef(false);
  // Update refs synchronously during render so event callbacks always see current values
  isFocusedRef.current = isFocused;
  showChecklistTooltipRef.current = showChecklistTooltip;
  meRef.current = me;
  const checklistTooltipDismissedKey = me?.id
    ? `checklist_onboarding_tooltip_dismissed_${me.id}`
    : null;

  const handleCloseAddMembersModal = useCallback(() => {
    setShowAddMembersModal(false);
    setIsInviteFromBanner(false);
  }, []);

  const navigateToMembersTab = useCallback(() => {
    tabNavigationRef.current?.navigate({
      name: 'members',
      params: { projectUUId },
      merge: true,
    } as any);
  }, [projectUUId]);

  const handleBannerInviteSent = useCallback(() => {
    setIsInviteFromBanner(false);
    setShowInviteNudge(false);

    if (me?.onboardingState?.inviteMemberComplete === false) {
      updateOnboardingState({
        variables: { attributes: { inviteMemberComplete: true } },
        update: (cache) => {
          if (!me?.id) return;
          cache.modify({
            id: cache.identify({ __typename: 'User', id: me.id }),
            fields: {
              onboardingState: (existing) => ({
                ...existing,
                inviteMemberComplete: true,
              }),
            },
          });
        },
      });
    }
    toast.show(
      <TaskCreatedToast
        title={t('addMembers.inviteSent')}
        message={t('nudge.gotCrewForThisJob.inviteSentMsg')}
        duration={3000}
        minWidth={388}
      />,
      {
        type: 'custom',
        duration: 3000,
        placement: 'bottom',
        animationType: 'slide-in',
        style: {
          marginBottom: 39,
          backgroundColor: 'transparent',
          shadowColor: 'transparent',
        },
      }
    );
    navigateToMembersTab();
  }, [t, toast, me, updateOnboardingState, navigateToMembersTab]);

  useEffect(() => {
    let fired = false;

    const completeIfNeeded = () => {
      if (fired) return;

      if (skipChecklistCompleteOnNextBlurRef.current) {
        skipChecklistCompleteOnNextBlurRef.current = false;
        return;
      }
      const isTooltipDismissed = () => {
        const userId = meRef.current?.id;
        if (!userId) return false;
        return (
          globalThis.localStorage?.getItem(
            `checklist_onboarding_tooltip_dismissed_${userId}`
          ) === 'true'
        );
      };
      if (
        showChecklistTooltipRef.current &&
        meRef.current?.onboardingState?.taskCreationComplete === true &&
        meRef.current?.onboardingState?.checklistCreationComplete === false &&
        !isTooltipDismissed()
      ) {
        fired = true;

        updateOnboardingState({
          variables: { attributes: { checklistCreationComplete: true } },
          update: (cache) => {
            const userId = meRef.current?.id;
            if (!userId) return;

            cache.modify({
              id: cache.identify({ __typename: 'User', id: userId }),
              fields: {
                onboardingState: (existing) => ({
                  ...existing,
                  checklistCreationComplete: true,
                }),
              },
            });
          },
        });
      }
    };

    const unsubscribe = navigation.addListener('blur', completeIfNeeded);

    return () => {
      unsubscribe();
      completeIfNeeded();
    };
  }, [navigation, updateOnboardingState]);

  useEffect(() => {
    const isTooltipDismissed = () =>
      !!checklistTooltipDismissedKey &&
      globalThis.localStorage?.getItem(checklistTooltipDismissedKey) === 'true';

    const checklistListener = DeviceEventEmitter.addListener(
      'SHOW_CHECKLIST_ONBOARDING_TOOLTIP',
      () => {
        if (isFocusedRef.current && !isTooltipDismissed()) {
          setShowChecklistTooltip(true);
        }
      }
    );
    const activeListener = DeviceEventEmitter.addListener(
      'TASK_NAME_TOOLTIP_ACTIVE',
      () => {
        isTaskNameTooltipShowingRef.current = true;
      }
    );
    const inactiveListener = DeviceEventEmitter.addListener(
      'TASK_NAME_TOOLTIP_INACTIVE',
      () => {
        isTaskNameTooltipShowingRef.current = false;
        if (
          isFocusedRef.current &&
          !isTooltipDismissed() &&
          me?.onboardingState?.taskCreationComplete === true &&
          me?.onboardingState?.checklistCreationComplete === false
        ) {
          setShowChecklistTooltip(true);
        }
      }
    );
    const checklistActionActiveListener = DeviceEventEmitter.addListener(
      'CHECKLIST_ACTION_TOOLTIP_ACTIVE',
      () => setIsChecklistActionTooltipActive(true)
    );
    const checklistActionInactiveListener = DeviceEventEmitter.addListener(
      'CHECKLIST_ACTION_TOOLTIP_INACTIVE',
      () => setIsChecklistActionTooltipActive(false)
    );
    return () => {
      checklistListener.remove();
      activeListener.remove();
      inactiveListener.remove();
      checklistActionActiveListener.remove();
      checklistActionInactiveListener.remove();
    };
  }, [
    checklistTooltipDismissedKey,
    me?.onboardingState?.taskCreationComplete,
    me?.onboardingState?.checklistCreationComplete,
  ]);

  useEffect(() => {
    const isDismissed =
      !!checklistTooltipDismissedKey &&
      globalThis.localStorage?.getItem(checklistTooltipDismissedKey) === 'true';
    if (
      isFocused &&
      !isDismissed &&
      me?.onboardingState?.taskCreationComplete === true &&
      me?.onboardingState?.checklistCreationComplete === false &&
      !showChecklistTooltip &&
      !isTaskNameTooltipShowingRef.current
    ) {
      setShowChecklistTooltip(true);
    }
  }, [
    isFocused,
    checklistTooltipDismissedKey,
    me?.onboardingState?.taskCreationComplete,
    me?.onboardingState?.checklistCreationComplete,
    showChecklistTooltip,
  ]);
  const isOnFirstTabOrCanShowInviteNudge = () => {
    if (globalThis.localStorage?.getItem('canShowInviteNudge') === 'true') {
      return true;
    }
    const stackState = navigation.getState();
    const currentRoute = stackState.routes[stackState.index];
    const tabState = currentRoute?.state;
    return (tabState?.index ?? 1) === 0;
  };

  // A later task can add another unjoined assignee while the banner is
  // already up; refresh so the copy switches to the multiple-members variant.
  useEffect(() => {
    if (!showInviteNudge) return;
    const listener = DeviceEventEmitter.addListener(
      'NON_MEMBER_ASSIGNEES_UPDATED',
      () =>
        setNonMemberAssigneeEmails(readStoredNonMemberAssignees(project?.id))
    );
    return () => listener.remove();
  }, [showInviteNudge, project?.id]);

  useEffect(() => {
    if (showInviteNudge && me?.onboardingState?.inviteMemberComplete === true) {
      globalThis.localStorage?.setItem('canShowInviteNudge', 'false');
      setShowInviteNudge(false);
    } else if (
      !showInviteNudge &&
      isFocused &&
      isOwner &&
      me?.onboardingState?.checklistCreationComplete === true &&
      me?.onboardingState?.inviteMemberComplete === false &&
      !showChecklistTooltip &&
      isOnFirstTabOrCanShowInviteNudge()
    ) {
      setNonMemberAssigneeEmails(readStoredNonMemberAssignees(project?.id));

      setShowInviteNudge(true);
      globalThis.localStorage?.setItem('canShowInviteNudge', 'true');
    }
  }, [
    isFocused,
    isOwner,
    showInviteNudge,
    showChecklistTooltip,
    me?.onboardingState?.inviteMemberComplete,
    me?.onboardingState?.checklistCreationComplete,
    navigation,
  ]);

  const handleEditProject = useCallback(() => {
    if (shouldShowPaywallForProject(project)) {
      setPaywallTeam(project?.team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }
    navigation.navigate('projects-stack', {
      screen: 'edit',
      params: { projectUUId },
    });
  }, [project, projectUUId, setPaywallTeam, setPaywallModalOpen, navigation]);

  const handleDuplicateProject = useCallback(() => {
    if (shouldShowPaywallForProject(project)) {
      setPaywallTeam(project?.team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }
    navigation.navigate('projects-stack', {
      screen: 'duplicate',
      params: { projectUUId },
    });
  }, [project, projectUUId, setPaywallTeam, setPaywallModalOpen, navigation]);

  const menuList = useMemo(
    () =>
      buildMenuList({
        isAdminOrHigher,
        isEditorOrHigher,
        isTeamOwnerOrAdmin,
        isTeamMember,
        isMember,
        hasMembers: !!members?.length,
        t,
        onEdit: handleEditProject,
        onDuplicate: handleDuplicateProject,
        onDelete: () => setShowModal(true),
      }),
    [
      isAdminOrHigher,
      isEditorOrHigher,
      isTeamOwnerOrAdmin,
      isTeamMember,
      isMember,
      members?.length,
      t,
      handleEditProject,
      handleDuplicateProject,
    ]
  );

  const deleteFunction = () => {
    return (
      <ConfirmModal
        showModal={true}
        isLoading={isDeletingProject}
        onClose={() => setShowModal(false)}
        onPress={() => {
          deleteProject({
            variables: {
              id: projectId ?? '',
            },
          });
        }}
        buttonText={t('deleteProject.title')}
        title={t('deleteProject.title')}
        message={t('deleteProject.message')}
      />
    );
  };

  const projectFilesCtxValue = useMemo(
    () => ({
      filesPanelRef: projectFilesRef,
      onSelectionStateChange: handleSelectionStateChange,
    }),
    [handleSelectionStateChange]
  );

  const minPanelWidth = windowWidth <= 1280 ? 639 : 742;

  const renderNonMemberTabBar = useCallback(
    (props: MaterialTopTabBarProps) => {
      return (
        <Box marginVertical='s'>
          <Box
            flexDirection='row'
            alignItems='center'
            justifyContent='space-between'
            borderBottomWidth={1}
            borderBottomColor='grey02'
            style={styles.topShadow}
            onLayout={(e) => setHeaderWidth(e.nativeEvent.layout.width)}>
            <Box
              flexDirection='row'
              alignItems='center'
              marginHorizontal='s'
              style={styles.topShadow}
              onLayout={(e) => setTabsWidth(e.nativeEvent.layout.width)}>
              <CustomTabBarProject
                {...props}
                spacing={theme.spacing.s}
                memberCount={project?.members?.length}
                hasUnreadActivity={(project?.unreadAuditsCount ?? 0) > 0}
                disabled
              />
            </Box>
            <Box flexDirection='row' alignItems='center' marginRight='m' />
          </Box>
        </Box>
      );
    },
    [project?.members?.length, project?.unreadAuditsCount]
  );

  if (showShellSkeleton) {
    return <ProjectDetailTabsSkeleton />;
  }

  if (modal) {
    return deleteFunction();
  }

  if (!project) {
    return (
      <Box flex={1} paddingTop='m' alignItems='center'>
        <Text>Project not found</Text>
      </Box>
    );
  }

  const isOnboardingThisProject =
    !!currentProject && currentProject.uuid === projectUUId;

  if (isMobileBrowser) {
    const hasPendingMobile =
      project?.currentUserHasPendingMemberAdditionRequest;
    return (
      <View style={{ flex: 1 }}>
        <ProjectWelcomeMobileScreen
          project={{ id: project.id, uuid: project.uuid, name: project.name }}
        />
        {/* "You've been added" card — shown once, cleared by useProjectOnboarding */}
        <Modal
          transparent
          visible={isOnboardingThisProject}
          animationType='slide'
          onRequestClose={() => currentProject && goToProject(currentProject)}>
          <Pressable
            style={styles.sheetOverlay}
            onPress={() => currentProject && goToProject(currentProject)}
          />
          <View style={styles.sheet}>
            <ProjectOnboardingCard
              projectId={project.id}
              projectName={project.name}
              onGoToProject={() =>
                currentProject && goToProject(currentProject)
              }
            />
          </View>
        </Modal>
        {/* "Request sent" card — shown once after expired-invite signup */}
        <Modal
          transparent
          visible={requestSentSheetVisible}
          animationType='slide'
          onRequestClose={() => setRequestSentSheetVisible(false)}>
          <Pressable
            style={styles.sheetOverlay}
            onPress={() => setRequestSentSheetVisible(false)}
          />
          <View style={styles.sheet}>
            <ProjectOnboardingCard
              projectId={project.id}
              projectName={project.name}
              requestSent
              onDismiss={() => setRequestSentSheetVisible(false)}
            />
          </View>
        </Modal>
        {/* "Download the App" banner — shown while membership is pending */}
        {hasPendingMobile && (
          <View style={styles.downloadBanner}>
            <Text variant='labelLarge' color='white' mb='xxs'>
              Hang tight.
            </Text>
            <Text
              variant='webBodySecondary'
              color='grey02'
              mb='m'
              style={{ lineHeight: 20 }}>
              Once approved, you&apos;ll get access to the project and chat.
              Download the app so you&apos;re ready to go.
            </Text>
            <Pressable
              style={styles.downloadBannerButton}
              onPress={() =>
                detectAppInstallation('com.eloveit.tasktag:///', APP_STORE_URL)
              }>
              <Text variant='labelLarge' color='white'>
                Download the App
              </Text>
            </Pressable>
          </View>
        )}
      </View>
    );
  }

  // Pre-computed for renderTabBar to avoid if/else inside it
  let circleInviteBgColor = theme.colors.white;
  if (project?.archivedAt) circleInviteBgColor = theme.colors.grey02;
  else if (inviteHovered) circleInviteBgColor = theme.colors.grey01;

  const paywallVisible = !!project && shouldShowPaywallForProject(project);
  const copyLinkTooltipVariant: 'dark' | 'light' =
    copyLinkTooltipMode === 'hover' ? 'dark' : 'light';
  const copyLinkTooltipTitle =
    copyLinkTooltipMode === 'copied' ? (
      <Box flexDirection='row'>
        <Icon name='CheckCircle' color='greenSecondary' variant='s' />
        <Text ml='xs' variant='labelSmall' color='black'>
          {t('shared:linkCopied')}
        </Text>
      </Box>
    ) : (
      <Text ml='xxs' variant='labelSmall' color='white'>
        Copy Link to Invite
      </Text>
    );

  const handleCopyLinkMouseEnter = () => {
    setIsCopyLinkHovered(true);
    if (!suppressHoverTooltip) setCopyLinkTooltipMode('hover');
  };
  const handleCopyLinkMouseLeave = () => {
    setIsCopyLinkHovered(false);
    setCopyLinkTooltipMode(null);
    setSuppressHoverTooltip(false);
  };
  const handleCopyLinkClick = () => {
    if (!project?.archivedAt) handleCreateInvite();
  };
  const handleCopyLinkKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!project?.archivedAt) handleCreateInvite();
    }
  };
  const handleInvitePress = () => {
    if (shouldShowPaywallForProject(project)) {
      setPaywallTeam(project?.team ?? undefined);
      setPaywallModalOpen(true);
      return;
    }
    setPassMemberAssigneeEmails(false);
    setShowAddMembersModal(true);
  };

  // Not memoized: this resolver depends on several values recomputed every
  // render (tooltip mode, hover state, paywall flag, etc.). Wrapping it in
  // useCallback with comprehensive deps would just look like memoization
  // without delivering it, so we leave it as a fresh closure each render.
  const renderTabBar = (props: MaterialTopTabBarProps) => {
    tabNavigationRef.current = props.navigation;
    return (
      <Box marginVertical='s'>
        <Box
          flexDirection='row'
          alignItems='center'
          justifyContent='space-between'
          borderBottomWidth={1}
          borderBottomColor='grey02'
          style={styles.topShadow}
          onLayout={(e) => setHeaderWidth(e.nativeEvent.layout.width)}>
          <Box
            flexDirection='row'
            alignItems='center'
            marginHorizontal='s'
            style={styles.topShadow}
            onLayout={(e) => setTabsWidth(e.nativeEvent.layout.width)}>
            <CustomTabBarProject
              {...props}
              spacing={theme.spacing.s}
              memberCount={project?.members?.length}
              hasUnreadActivity={(project?.unreadAuditsCount ?? 0) > 0}
              checklistTabRef={checklistTabRef}
            />
            <TooltipOnboardingWeb
              variant='bottom-left'
              tooltipStyle='success'
              title={t('projects.checklist.onboardingTooltip.title')}
              description={t(
                'projects.checklist.onboardingTooltip.description'
              )}
              step={t('projects.checklist.onboardingTooltip.step')}
              ctaText={t('projects.checklist.onboardingTooltip.ctaText')}
              onCtaPress={() => {
                skipChecklistCompleteOnNextBlurRef.current = true;

                setShowChecklistTooltip(false);
                if (checklistTooltipDismissedKey) {
                  globalThis.localStorage?.setItem(
                    checklistTooltipDismissedKey,
                    'true'
                  );
                }
                props.navigation.navigate({
                  name: 'checklist',
                  params: { projectUUId, showActionTooltip: true },
                  merge: true,
                } as any);
              }}
              open={onboardingActive && isFocused && showChecklistTooltip}
              forceShow={onboardingActive && isFocused && showChecklistTooltip}
              arrowAtTriggerCenter
              externalTriggerRef={checklistTabRef}
              style={{ width: 0, height: 0, overflow: 'hidden' }}>
              <View style={{ width: 0, height: 0 }} />
            </TooltipOnboardingWeb>
          </Box>
          <Box flexDirection='row' alignItems='center' marginRight='m'>
            {(isEditorOrHigher || isTeamMember || isTeamOwnerOrAdmin) && (
              <>
                <Box position='relative' marginRight='xs'>
                  <TooltipWebV3
                    marginOffset={8}
                    open={copyLinkTooltipMode !== null}
                    disableHoverListener
                    disableFocusListener
                    disableTouchListener
                    placement='bottom-start'
                    variant={copyLinkTooltipVariant}
                    title={copyLinkTooltipTitle}>
                    <div
                      aria-label='Copy Link to Invite'
                      role='button'
                      tabIndex={0}
                      onMouseEnter={handleCopyLinkMouseEnter}
                      onMouseLeave={handleCopyLinkMouseLeave}
                      style={{
                        display: 'inline-flex',
                        cursor: project?.archivedAt ? 'default' : 'pointer',
                      }}
                      onClick={handleCopyLinkClick}
                      onKeyDown={handleCopyLinkKeyDown}>
                      <Box
                        padding='xxs'
                        borderRadius='xs'
                        backgroundColor={
                          isCopyLinkHovered && !project?.archivedAt
                            ? 'grey01'
                            : undefined
                        }>
                        <Icon
                          name='LinkNew'
                          color={project?.archivedAt ? 'grey04' : 'grey05'}
                          style={{ width: 24, height: 24 }}
                        />
                      </Box>
                    </div>
                  </TooltipWebV3>
                </Box>
                <UpgradePaywallTooltip show={paywallVisible}>
                  <TooltipWebV3
                    title={
                      <Text variant='labelSmall' color='white'>
                        Invite member to this project
                      </Text>
                    }
                    placement='bottom-end'
                    variant='dark'
                    disableHoverListener={paywallVisible}>
                    <InviteButtonContent
                      mainPanelWidth={mainPanelWidth}
                      archivedAt={!!project?.archivedAt}
                      inviteHovered={inviteHovered}
                      circleInviteBgColor={circleInviteBgColor}
                      onPress={handleInvitePress}
                      onMouseEnter={() => setInviteHovered(true)}
                      onMouseLeave={() => setInviteHovered(false)}
                      addMembersLabel={t('shared:addMembers')}
                      inviteLabel={t('shared:invite')}
                    />
                  </TooltipWebV3>
                </UpgradePaywallTooltip>
              </>
            )}
          </Box>
        </Box>
        {onboardingActive &&
          showInviteNudge &&
          isOwner &&
          !showChecklistTooltip &&
          !isChecklistActionTooltipActive && (
            <GotCrewForThisJobBanner
              setShowInviteNudge={(show) => {
                setShowInviteNudge(show);
                if (!show) setNonMemberAssigneeEmails([]);
              }}
              onAddPeoplePress={() => {
                if (shouldShowPaywallForProject(project)) {
                  setPaywallTeam(project?.team ?? undefined);
                  setPaywallModalOpen(true);
                  return;
                }
                setPassMemberAssigneeEmails(true);
                setNonMemberAssigneeEmails(
                  readStoredNonMemberAssignees(project?.id)
                );
                setIsInviteFromBanner(true);
                setShowAddMembersModal(true);
              }}
              description={getNonMemberBannerDescription(
                nonMemberAssigneeEmails,
                t
              )}
            />
          )}
      </Box>
    );
  };

  if (!isMemberOrOwner && !isTeamOwnerOrAdmin && !isTeamMember) {
    const hasPending = project?.currentUserHasPendingMemberAdditionRequest;
    return (
      <Box flex={1} mt='m' minWidth={minPanelWidth}>
        <HyperLink>
          <Text
            accessibilityLabel='Description title'
            variant='bodySecondary'
            marginBottom='m'
            marginHorizontal='l'
            color='grey05'>
            {description}
          </Text>
        </HyperLink>
        <Tabs.Navigator
          initialRouteName='tasks'
          screenOptions={{ swipeEnabled: false }}
          tabBar={renderNonMemberTabBar}>
          <Tabs.Screen
            name='tasks'
            component={() => (
              <InviteRequestButton
                currentUserHasPendingMemberAdditionRequest={hasPending}
                projectId={projectId}
              />
            )}
            options={{
              tabBarLabel: t('projects.tasksWithCount', {
                context: 'guest',
              }),
            }}
          />
          <Tabs.Screen
            name='files'
            options={{ tabBarLabel: 'Files & Media' }}
            component={() => <Box />}
          />
          <Tabs.Screen
            name='activity-log'
            component={() => <Box />}
            options={{
              tabBarLabel: 'Activity Log',
            }}
          />
          <Tabs.Screen
            name='members'
            component={() => <Box />}
            options={{
              tabBarLabel: t('projects.membersWithCount', {
                context: 'guest',
              }),
            }}
          />
        </Tabs.Navigator>

        {/* Request Sent bottom sheet */}
        <Modal
          transparent
          visible={requestSentSheetVisible}
          animationType='slide'
          onRequestClose={() => setRequestSentSheetVisible(false)}>
          <Pressable
            style={styles.sheetOverlay}
            onPress={() => setRequestSentSheetVisible(false)}
          />
          <View style={styles.sheet}>
            <View style={styles.sheetHandle} />
            <View style={styles.sheetBody}>
              <View style={styles.clockCircle}>
                <Icon name='Clock' width={28} height={28} color='grey05' />
              </View>
              <Text
                variant='labelLarge'
                color='textPrimary'
                style={{ marginBottom: theme.spacing.xs, textAlign: 'center' }}>
                Request Sent
              </Text>
              <Text
                variant='webBodySecondary'
                color='textSecondary'
                style={{ textAlign: 'center', lineHeight: 22 }}>
                You'll be notified when it's approved.
              </Text>
            </View>
            <Button
              variant='primary'
              onPress={() => setRequestSentSheetVisible(false)}
              style={{
                backgroundColor: theme.colors.black,
                borderRadius: 12,
              }}>
              Got It
            </Button>
          </View>
        </Modal>
      </Box>
    );
  }
  return (
    <Box flex={1} mt='xs' minWidth={minPanelWidth}>
      {description?.length > 0 && (
        <HyperLink>
          <Box
            flexDirection='column'
            marginTop='xxs'
            backgroundColor='grey01'
            borderRadius='s'
            padding='m'
            mx='s'
            marginBottom='xs'>
            <Text variant='labelEmphasized' color='textSecondary'>
              Description
            </Text>
            <Text
              accessibilityLabel='Description title'
              variant='webBodySecondary'
              marginTop='m'
              color='textSecondary'>
              {description}
            </Text>
          </Box>
        </HyperLink>
      )}
      <ProjectFilesScreenContext.Provider value={projectFilesCtxValue}>
        <Tabs.Navigator
          initialRouteName='tasks'
          screenOptions={{ swipeEnabled: false }}
          tabBar={renderTabBar}
          threeDotMenuList={menuList}>
          <>
            <Tabs.Screen
              name='tasks'
              component={ProjectTasks}
              initialParams={{ projectUUId }}
              navigationKey={`tasks-${projectUUId}`}
              options={{
                tabBarLabel: t('projects.tasksWithCount', {
                  context: 'guest',
                }),
              }}
            />
            <Tabs.Screen
              name='checklist'
              component={ProjectChecklistPage}
              initialParams={{ projectUUId }}
              navigationKey={`Checklist-${projectUUId}`}
              options={{
                tabBarLabel: t('projects.checklist.title'),
              }}
            />
            <Tabs.Screen
              name='files'
              options={{ tabBarLabel: 'Files & Media', lazy: true }}
              component={ProjectFilesScreen}
              initialParams={{ projectUUId }}
              navigationKey={`files-${projectUUId}`}
            />
            <Tabs.Screen
              name='activity-log'
              component={ProjectActivityLog}
              initialParams={{ projectUUId }}
              navigationKey={`activity-log-${projectUUId}`}
              options={{
                tabBarLabel: 'Activity Log',
                lazy: true,
              }}
            />
            <Tabs.Screen
              name='members'
              initialParams={{ projectUUId }}
              navigationKey={`members-${projectUUId}`}
              options={{
                tabBarLabel: t('projects.membersWithCount', {
                  context: 'guest',
                }),
              }}>
              {() => (
                <ErrorBoundary>
                  <ProjectMembers
                    externalShowAddMembersModal={showAddMembersModal}
                    onExternalModalClose={handleCloseAddMembersModal}
                    onInvitePress={handleInvitePress}
                    onInviteSent={
                      isInviteFromBanner
                        ? handleBannerInviteSent
                        : navigateToMembersTab
                    }
                    initialInviteEmails={
                      passMemberAssigneeEmails ? nonMemberAssigneeEmails : []
                    }
                  />
                </ErrorBoundary>
              )}
            </Tabs.Screen>
          </>
        </Tabs.Navigator>
      </ProjectFilesScreenContext.Provider>
      {showBanner && !project?.team?.personal && (
        <Box style={styles.bannerContainer}>
          <ProjectDetailBanner
            planName={project?.team?.name ?? 'Team'}
            expiredDate={
              project?.team?.subscriptionDetails?.expirationDate
                ? new Date(project?.team?.subscriptionDetails?.expirationDate)
                : undefined
            }
            daysRemaining={
              project?.team?.subscriptionDetails?.subscriptionStatus ===
                'canceled' ||
              project?.team?.subscriptionDetails?.subscriptionStatus ===
                'incomplete_expired'
                ? -1
                : daysRemaining
            }
            onRemindLater={() => {
              setShowBanner(false);
            }}
            onUpgradeNow={() => {
              goToUpgradeSubscription(project?.team);
            }}
            isOwner={
              project?.team?.currentUserRole === TeamMemberRole.Owner ||
              project?.team?.owner?.id === me?.id
            }
            isOwnerOrAdmin={
              project?.team?.currentUserRole === TeamMemberRole.Owner ||
              project?.team?.currentUserRole === TeamMemberRole.Admin ||
              project?.team?.owner?.id === me?.id
            }
            teamId={project?.team?.id ?? undefined}
            teamOwnerId={project?.team?.owner?.id ?? undefined}
            teamOwnerName={project?.team?.owner?.name ?? undefined}
          />
        </Box>
      )}
      {projectFilesState && (
        <ProjectFilesSelectionBar
          isSelectMode={projectFilesState.isSelectMode}
          selectedDocuments={projectFilesState.selectedDocuments}
          setIsSelectMode={projectFilesState.setIsSelectMode}
          setSelectedDocuments={projectFilesState.setSelectedDocuments}
          isTeamOwnerOrAdmin={projectFilesState.isTeamOwnerOrAdmin}
          project={projectFilesState.project}
          downloadFiles={projectFilesState.downloadFiles}
          isDownloading={projectFilesState.isDownloading}
          shareLoading={projectFilesState.shareLoading}
          showLinkCopied={projectFilesState.showLinkCopied}
          handleSharePress={projectFilesState.handleSharePress}
          setShowDeleteModal={projectFilesState.setShowDeleteModal}
        />
      )}
      <Toast ref={toastRef} />
      <BottomToast ref={copyFailedToastRef} />
      {showProjectCreatedModal && (
        <ProjectCreatedModalWeb
          onDismiss={() => setShowProjectCreatedModal(false)}
        />
      )}
    </Box>
  );
};

const styles = StyleSheet.create({
  topShadow: {
    backgroundColor: 'white',
  },
  contentContainerPanelStyle: {
    paddingHorizontal: theme.spacing.l,
    // paddingVertical: 4,
    backgroundColor: theme.colors.white,
  },
  bannerContainer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    zIndex: 100,
  },
  sheetOverlay: {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: theme.colors.grey06,
  },
  sheet: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.white,
    borderTopLeftRadius: theme.borderRadii.l,
    borderTopRightRadius: theme.borderRadii.l,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
    paddingBottom: theme.spacing.xxl,
  },
  sheetHandle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    backgroundColor: theme.colors.grey02,
    borderRadius: theme.borderRadii.xxxs,
    marginBottom: theme.spacing.m,
  },
  sheetBody: {
    alignItems: 'center',
    paddingVertical: theme.spacing.xs,
    marginBottom: theme.spacing.l,
  },
  clockCircle: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: theme.colors.grey01,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing.s,
  },
  downloadBanner: {
    position: 'absolute' as const,
    bottom: theme.spacing.m,
    left: theme.spacing.m,
    right: theme.spacing.m,
    backgroundColor: theme.colors.black,
    borderRadius: theme.borderRadii.s,
    padding: theme.spacing.m,
    zIndex: 10,
  },
  downloadBannerButton: {
    backgroundColor: theme.colors.greenSecondary,
    borderRadius: theme.borderRadii.xs,
    height: theme.spacing.xxl,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
